import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Target, Plus, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OKRsPage = () => {
  const { user } = useAuth();
  const [okrs, setOkrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [quarterFilter, setQuarterFilter] = useState('all');
  const [newOkr, setNewOkr] = useState({
    objective: '',
    key_results: [{ title: '', target: '', progress: 0 }],
    quarter: `Q${Math.ceil((new Date().getMonth() + 1) / 3)}-${new Date().getFullYear()}`
  });

  const fetchOkrs = useCallback(async () => {
    try {
      let url = `${API_URL}/api/okrs`;
      if (quarterFilter !== 'all') url += `?quarter=${quarterFilter}`;
      const response = await axios.get(url, { withCredentials: true });
      setOkrs(response.data);
    } catch (error) {
      console.error('Fetch OKRs error:', error);
      toast.error('Failed to load OKRs');
    } finally {
      setLoading(false);
    }
  }, [quarterFilter]);

  useEffect(() => {
    fetchOkrs();
  }, [fetchOkrs]);

  const handleAddOkr = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/okrs`, {
        ...newOkr,
        employee_id: user.id,
        key_results: newOkr.key_results.map(kr => ({ ...kr, progress: 0 }))
      }, { withCredentials: true });
      toast.success('OKR created');
      setIsAddDialogOpen(false);
      setNewOkr({ objective: '', key_results: [{ title: '', target: '', progress: 0 }], quarter: newOkr.quarter });
      fetchOkrs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create OKR');
    }
  };

  const handleProgressUpdate = async (okrId, krIndex, progress) => {
    try {
      await axios.put(`${API_URL}/api/okrs/${okrId}/progress`, {
        key_result_index: krIndex,
        progress: parseFloat(progress)
      }, { withCredentials: true });
      toast.success('Progress updated');
      fetchOkrs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update progress');
    }
  };

  const addKeyResult = () => {
    setNewOkr({
      ...newOkr,
      key_results: [...newOkr.key_results, { title: '', target: '', progress: 0 }]
    });
  };

  const updateKeyResult = (index, field, value) => {
    const updated = [...newOkr.key_results];
    updated[index][field] = value;
    setNewOkr({ ...newOkr, key_results: updated });
  };

  const quarters = ['Q1-2025', 'Q2-2025', 'Q3-2025', 'Q4-2025', 'Q1-2026'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="okrs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="w-7 h-7 text-primary" />
            OKRs & Goals
          </h1>
          <p className="text-muted-foreground">Set and track Objectives and Key Results</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-okr-btn"><Plus className="w-4 h-4" />Create OKR</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Create New OKR</DialogTitle></DialogHeader>
            <form onSubmit={handleAddOkr} className="space-y-4">
              <div className="space-y-2">
                <Label>Quarter</Label>
                <Select value={newOkr.quarter} onValueChange={(v) => setNewOkr({ ...newOkr, quarter: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {quarters.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Objective</Label>
                <Textarea
                  value={newOkr.objective}
                  onChange={(e) => setNewOkr({ ...newOkr, objective: e.target.value })}
                  placeholder="What do you want to achieve?"
                  required
                  data-testid="okr-objective"
                />
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Key Results</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addKeyResult}>+ Add KR</Button>
                </div>
                {newOkr.key_results.map((kr, i) => (
                  <div key={i} className="space-y-2 p-3 bg-muted rounded-md">
                    <Input
                      placeholder={`Key Result ${i + 1}`}
                      value={kr.title}
                      onChange={(e) => updateKeyResult(i, 'title', e.target.value)}
                      required
                    />
                    <Input
                      placeholder="Target (e.g., 100 users, 50% improvement)"
                      value={kr.target}
                      onChange={(e) => updateKeyResult(i, 'target', e.target.value)}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-okr">Create OKR</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={quarterFilter} onValueChange={setQuarterFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Filter by quarter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Quarters</SelectItem>
            {quarters.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* OKRs List */}
      <div className="space-y-4">
        {okrs.map((okr) => (
          <Card key={okr.id}>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Badge variant="outline" className="mb-2">{okr.quarter}</Badge>
                  <CardTitle className="text-lg">{okr.objective}</CardTitle>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Overall Progress</p>
                  <p className="text-2xl font-bold">{Math.round(okr.overall_progress || 0)}%</p>
                </div>
              </div>
              <Progress value={okr.overall_progress || 0} className="h-2 mt-2" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {okr.key_results?.map((kr, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 bg-muted rounded-md">
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{kr.title}</p>
                      {kr.target && <p className="text-xs text-muted-foreground">Target: {kr.target}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={kr.progress || 0}
                        onChange={(e) => handleProgressUpdate(okr.id, i, e.target.value)}
                        className="w-20 h-8 text-center"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {okrs.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No OKRs found. Create your first OKR to get started!
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OKRsPage;
